import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  agentHealthApi,
  type AgentHealthRow,
  type HealthAlert,
  type VmActionResult,
} from "@/api/agent-health";
import {
  Server,
  HardDrive,
  MemoryStick,
  Activity,
  RefreshCw,
  Terminal,
  Trash2,
  RotateCw,
  FileText,
  AlertTriangle,
} from "lucide-react";

const alertVariant: Record<HealthAlert, "success" | "warning" | "danger" | "secondary"> = {
  ok: "success",
  warn: "warning",
  critical: "danger",
  unreachable: "secondary",
};

function fmtUptime(s: number | null): string {
  if (!s || s <= 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function diskBarColor(pct: number | null): string {
  if (pct === null) return "bg-text-muted";
  if (pct >= 92) return "bg-red-500";
  if (pct >= 80) return "bg-orange-500";
  if (pct >= 60) return "bg-yellow-500";
  return "bg-green-500";
}

interface HostGroup {
  host: string;
  worst: HealthAlert;
  rep: AgentHealthRow; // representative row (shared VM metrics)
  agents: AgentHealthRow[];
}

const ALERT_RANK: Record<HealthAlert, number> = { unreachable: 0, critical: 1, warn: 2, ok: 3 };

const ACTION_META: Record<string, { icon: typeof Terminal; destructive?: boolean }> = {
  df: { icon: HardDrive },
  "session-usage": { icon: FileText },
  "prune-sessions": { icon: Trash2 },
  "tail-journal": { icon: Terminal },
  "restart-runtime": { icon: RotateCw, destructive: true },
};

function VmCard({ group }: { group: HostGroup }) {
  const { rep, agents, host } = group;
  const [output, setOutput] = useState<VmActionResult | null>(null);

  const { data: actionsData } = useQuery({
    queryKey: ["agent-health-actions"],
    queryFn: agentHealthApi.actions,
    staleTime: 5 * 60_000,
  });

  const runAction = useMutation({
    mutationFn: (action: string) => agentHealthApi.runAction(rep.agentId, action),
    onSuccess: (res) => setOutput(res),
  });

  const onAction = (id: string, label: string, destructive?: boolean) => {
    if (destructive && !window.confirm(`${label} on ${host}? This affects every agent on this VM.`)) return;
    setOutput(null);
    runAction.mutate(id);
  };

  const disk = rep.diskPct;
  const memPct =
    rep.memUsedMb && rep.memTotalMb ? Math.round((rep.memUsedMb / rep.memTotalMb) * 100) : null;

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              {group.worst === "critical" || group.worst === "unreachable" ? (
                <AlertTriangle size={20} className="text-red-500" />
              ) : (
                <Server size={20} className="text-accent" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary font-mono">{host}</p>
              <p className="text-xs text-text-muted">
                {rep.companyName} · {agents.length} agent{agents.length !== 1 ? "s" : ""} ·{" "}
                {rep.adapterType.replace("_", " ")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={rep.gatewayUp === false ? "danger" : "success"}>
              <Activity size={12} className="mr-1" />
              {rep.gatewayUp === false ? "gateway down" : "gateway up"}
            </Badge>
            <Badge variant={alertVariant[group.worst]}>{group.worst}</Badge>
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <div className="flex items-center justify-between text-text-muted mb-1">
              <span className="flex items-center gap-1"><HardDrive size={12} /> Disk</span>
              <span className="text-text-secondary">
                {disk !== null ? `${disk}%` : "—"}
                {rep.diskAvailMb !== null ? ` · ${(rep.diskAvailMb / 1024).toFixed(1)}G free` : ""}
              </span>
            </div>
            <div className="h-1.5 w-full rounded bg-border overflow-hidden">
              <div className={`h-full ${diskBarColor(disk)}`} style={{ width: `${disk ?? 0}%` }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-text-muted mb-1">
              <span className="flex items-center gap-1"><MemoryStick size={12} /> Memory</span>
              <span className="text-text-secondary">
                {rep.memUsedMb !== null && rep.memTotalMb !== null
                  ? `${(rep.memUsedMb / 1024).toFixed(1)}/${(rep.memTotalMb / 1024).toFixed(1)}G`
                  : "—"}
              </span>
            </div>
            <div className="h-1.5 w-full rounded bg-border overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${memPct ?? 0}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-text-muted">
            <Activity size={12} /> Uptime
            <span className="text-text-secondary ml-auto">{fmtUptime(rep.uptimeS)}</span>
          </div>
        </div>

        {rep.probeError && (
          <p className="mt-2 text-xs text-red-500 font-mono">Probe error: {rep.probeError}</p>
        )}

        {/* Agents on host */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {agents.map((a) => (
            <span
              key={a.agentId}
              className="inline-flex items-center rounded bg-surface px-2 py-0.5 text-xs text-text-secondary"
            >
              {a.agentName}
            </span>
          ))}
        </div>

        {/* Management actions */}
        <div className="mt-3 border-t border-border pt-3 flex flex-wrap gap-2">
          {(actionsData?.actions ?? []).map((act) => {
            const meta = ACTION_META[act.id] ?? { icon: Terminal };
            const Icon = meta.icon;
            return (
              <Button
                key={act.id}
                size="sm"
                variant={meta.destructive ? "outline" : "ghost"}
                disabled={runAction.isPending}
                onClick={() => onAction(act.id, act.label, meta.destructive)}
                className={meta.destructive ? "text-red-500" : ""}
              >
                <Icon size={14} className="mr-1" />
                {act.label}
              </Button>
            );
          })}
          {runAction.isPending && <RefreshCw size={14} className="animate-spin text-text-muted self-center" />}
        </div>

        {output && (
          <div className="mt-3 rounded-md bg-surface px-3 py-2">
            <p className="text-xs font-medium text-text-muted mb-1">
              {output.label} · {output.success ? "ok" : "failed"}
            </p>
            <pre className="text-xs text-text-secondary font-mono whitespace-pre-wrap max-h-64 overflow-auto">
              {output.output || "(no output)"}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AgentHealth() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["agent-health"],
    queryFn: agentHealthApi.list,
    refetchInterval: 30_000,
  });

  // Group agents by VM host; pick the worst alert + a representative row.
  const groups: HostGroup[] = [];
  const byHost = new Map<string, AgentHealthRow[]>();
  for (const row of data?.agents ?? []) {
    const key = row.vmHost ?? "unknown";
    const arr = byHost.get(key) ?? [];
    arr.push(row);
    byHost.set(key, arr);
  }
  for (const [host, rows] of byHost) {
    const worst = rows.reduce<HealthAlert>(
      (w, r) => (ALERT_RANK[r.alert] < ALERT_RANK[w] ? r.alert : w),
      "ok",
    );
    groups.push({ host, worst, rep: rows[0]!, agents: rows });
  }
  groups.sort((a, b) => ALERT_RANK[a.worst] - ALERT_RANK[b.worst] || (b.rep.diskPct ?? 0) - (a.rep.diskPct ?? 0));

  const summary = data?.summary ?? {};
  const attention = (summary.critical ?? 0) + (summary.unreachable ?? 0) + (summary.warn ?? 0);

  return (
    <CenterPanel title="Agent Health">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-secondary">
          {data?.total ?? 0} agent{(data?.total ?? 0) !== 1 ? "s" : ""} across {byHost.size} VM
          {byHost.size !== 1 ? "s" : ""}
          {attention > 0 ? (
            <span className="ml-2 text-orange-500 font-medium">· {attention} need attention</span>
          ) : (
            <span className="ml-2 text-green-500">· all healthy</span>
          )}
        </p>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? "animate-spin mr-1" : "mr-1"} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-text-muted">Loading health…</CardContent>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Server size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              No health samples yet. The collector probes every 5 minutes — check back shortly.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <VmCard key={g.host} group={g} />
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
